import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { forwardRef, useRef, useState, useMemo, useEffect, cloneElement } from 'react';
import styled from 'styled-components';
import { typographyTemplate, spacingsTemplate, bordersTemplate, mergeRefs, useIsInDialog } from '@equinor/eds-utils';
import { tooltip } from './Tooltip.tokens.js';
import { useFloating, offset, flip, shift, arrow, autoUpdate, useInteractions, useHover, useFocus, useRole, useDismiss, FloatingPortal } from '@floating-ui/react';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';

var _excluded = ["title", "placement", "children", "style", "enterDelay"];
var StyledTooltip = styled.div.withConfig({
  displayName: "Tooltip__StyledTooltip",
  componentId: "sc-m2im2p-0"
})(["", " ", " ", " background:", ";z-index:1500;white-space:nowrap;&::before{content:'; Has tooltip: ';clip-path:inset(50%);height:1px;width:1px;margin:-1px;overflow:hidden;padding:0;position:absolute;}"], typographyTemplate(tooltip.typography), spacingsTemplate(tooltip.spacings), bordersTemplate(tooltip.border), tooltip.background);
var ArrowWrapper = styled.div.withConfig({
  displayName: "Tooltip__ArrowWrapper",
  componentId: "sc-m2im2p-1"
})(["position:absolute;width:", ";height:", ";z-index:-1;"], tooltip.entities.arrow.width, tooltip.entities.arrow.height);
var TooltipArrow = styled.svg.withConfig({
  displayName: "Tooltip__TooltipArrow",
  componentId: "sc-m2im2p-2"
})(["width:", ";height:", ";position:absolute;fill:", ";"], tooltip.entities.arrow.width, tooltip.entities.arrow.height, tooltip.background);
var Tooltip = /*#__PURE__*/forwardRef(function Tooltip(_ref, ref) {
  var title = _ref.title,
    _ref$placement = _ref.placement,
    placement = _ref$placement === void 0 ? 'bottom' : _ref$placement,
    children = _ref.children,
    style = _ref.style,
    _ref$enterDelay = _ref.enterDelay,
    enterDelay = _ref$enterDelay === void 0 ? 100 : _ref$enterDelay,
    rest = _objectWithoutProperties(_ref, _excluded);
  var arrowRef = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  var shouldOpen = title !== '';
  var _useFloating = useFloating({
      placement: placement,
      open: open,
      onOpenChange: setOpen,
      middleware: [offset(14), flip(), shift({
        padding: 8
      }), arrow({
        element: arrowRef
      })],
      whileElementsMounted: autoUpdate
    }),
    x = _useFloating.x,
    y = _useFloating.y,
    refs = _useFloating.refs,
    strategy = _useFloating.strategy,
    context = _useFloating.context,
    _useFloating$middlewa = _useFloating.middlewareData.arrow,
    _useFloating$middlewa2 = _useFloating$middlewa === void 0 ? {} : _useFloating$middlewa,
    arrowX = _useFloating$middlewa2.x,
    arrowY = _useFloating$middlewa2.y,
    finalPlacement = _useFloating.placement;
  var anchorRef = useMemo(function () {
    return mergeRefs(refs.setReference, children === null || children === void 0 ? void 0 : children.ref);
  }, [refs.setReference, children === null || children === void 0 ? void 0 : children.ref]);
  var tooltipRef = useMemo(function () {
    return mergeRefs(refs.setFloating, ref);
  }, [refs.setFloating, ref]);
  var _useInteractions = useInteractions([useHover(context, {
      delay: {
        open: enterDelay
      }
    }), useFocus(context), useRole(context, {
      role: 'tooltip'
    }), useDismiss(context)]),
    getReferenceProps = _useInteractions.getReferenceProps,
    getFloatingProps = _useInteractions.getFloatingProps;
  useEffect(function () {
    var staticSide = {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right'
    }[finalPlacement.split('-')[0]];
    var arrowTransform = 'none';
    switch (staticSide) {
      case 'right':
        arrowTransform = 'rotateY(180deg)';
        break;
      case 'left':
        arrowTransform = 'none';
        break;
      case 'top':
        arrowTransform = 'rotate(90deg)';
        break;
      case 'bottom':
        arrowTransform = 'rotate(-90deg)';
        break;
    }
    if (arrowRef.current) {
      var _Object$assign;
      Object.assign(arrowRef.current.style, (_Object$assign = {
        left: arrowX != null ? "".concat(arrowX, "px") : '',
        top: arrowY != null ? "".concat(arrowY, "px") : '',
        right: '',
        bottom: ''
      }, _defineProperty(_Object$assign, staticSide, '-6px'), _defineProperty(_Object$assign, "transform", arrowTransform), _Object$assign));
    }
  });
  var updatedChildren = /*#__PURE__*/cloneElement(children, _objectSpread({}, getReferenceProps(_objectSpread({
    ref: anchorRef
  }, children.props))));

  //temporary fix for tooltip inside dialog. Should be replaced by popover api when it is ready
  var inDialog = useIsInDialog(refs.domReference.current);
  var TooltipEl = /*#__PURE__*/jsxs(StyledTooltip, _objectSpread(_objectSpread(_objectSpread({}, rest), getFloatingProps({
    ref: tooltipRef,
    style: _objectSpread(_objectSpread({}, style), {}, {
      position: strategy,
      top: y || 0,
      left: x || 0
    })
  })), {}, {
    children: [title, /*#__PURE__*/jsx(ArrowWrapper, {
      ref: arrowRef,
      children: /*#__PURE__*/jsx(TooltipArrow, {
        className: "arrowSvg",
        children: /*#__PURE__*/jsx("path", {
          d: "M0.504838 4.86885C-0.168399 4.48524 -0.168399 3.51476 0.504838 3.13115L6 8.59227e-08L6 8L0.504838 4.86885Z"
        })
      })
    })]
  }));
  return /*#__PURE__*/jsxs(Fragment, {
    children: [inDialog ? /*#__PURE__*/jsx(Fragment, {
      children: shouldOpen && open && TooltipEl
    }) : /*#__PURE__*/jsx(FloatingPortal, {
      id: "eds-tooltip-container",
      children: shouldOpen && open && TooltipEl
    }), updatedChildren]
  });
});

export { Tooltip };
